import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DeleteButton } from '@/components/Elements/Button'
import DraggableItem from '@/components/Elements/DraggableItem'
import { TableType } from '@/features/tableProfiles/tableProfile.enum'
import useUserTableProfile from '@/features/userTableProfiles/useUserTableProfile'
import { authSelector } from '@/stores/auth/authSlice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { Box, Button, Flex, Spacer } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import BaseOfferTaskWidget from './BaseOfferTaskWidget'
import { FaRegStar, FaStar } from 'react-icons/fa'

const MySourcingWidget = () => {
  const [shownProfiles, setShownProfiles] = useState([])
  const { user } = useSelector(authSelector)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const {
    useGetMyTableProfiles,
    useDeleteTableProfile,
    useToggleShownProfileInSidebar,
    useMakeDefaultProfile,
    useUpdateUserTableProfile,
  } = useUserTableProfile()

  const { data: userTableProfileResponse, isLoading: isLoadingTableProfile } = useGetMyTableProfiles(
    TableType.SOURCING_OFFER_LIST
  )

  const deleteTableProfile = useDeleteTableProfile()
  const toggleShownProfileInSidebar = useToggleShownProfileInSidebar()
  const makeDefaultProfile = useMakeDefaultProfile()
  const updateUserTableProfile = useUpdateUserTableProfile()

  const { open: openConfirmationModal } = useConfirmationModalManagement()

  //Translated Variables

  const shownProfileInSidebar = userTableProfileResponse?.data?.profilesShownInSidebar || []
  const defaultProfileId = userTableProfileResponse?.data?.defaultProfile?._id || ''

  useEffect(() => {
    if (Array.isArray(userTableProfileResponse?.data?.profiles)) {
      const profiles = userTableProfileResponse?.data?.profiles || []
      const profilesShownInDashboard = userTableProfileResponse?.data?.profilesShownInDashboard ?? []
      const shownProfiles = profiles.filter(
        (profile) =>
          Array.isArray(userTableProfileResponse?.data?.profilesShownInDashboard) &&
          userTableProfileResponse?.data?.profilesShownInDashboard.includes(profile._id)
      )

      setShownProfiles(shownProfiles)
    }
  }, [userTableProfileResponse?.data])

  const handleGoToProfile = (profileId) => {
    if (!profileId) {
      toast.error("Can't go to this profile, missing profile id ! ")
    }
    navigate('/offers/mySourcing?profileId=' + profileId)
  }

  const handleDeleteProfile = async (profileId, profileName) => {
    try {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Delete Table Profile ',
        message: `Are you sure you want to delete this profile - ${profileName}?`,
      })

      if (!isConfirmed) {
        return
      }
      dispatch(showLoadingOverlay())

      const userTableProfile = userTableProfileResponse?.data?._id
      console.log('userTableProfile', userTableProfile)

      if (!userTableProfile) {
        throw new Error('User Table Profile id not found.')
      }

      const res = await deleteTableProfile.mutateAsync({
        id: userTableProfileResponse?.data?._id,
        tableProfileId: profileId,
      })
    } catch (error) {
      toast.error('Error deleting profile: ' + error.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleToggleShownProfileInSidebar = async (profileId) => {
    try {
      dispatch(showLoadingOverlay())

      const userTableProfileId = userTableProfileResponse?.data?._id

      if (!userTableProfileId) {
        throw new Error('User Table Profile id not found.')
      }

      const res = await toggleShownProfileInSidebar.mutateAsync({
        id: userTableProfileId,
        tableProfileId: profileId,
      })
    } catch (error) {
      toast.error('Error toggling table profile in sidebar: ' + error.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = shownProfiles.findIndex((item) => item.id === active.id)
      const newIndex = shownProfiles.findIndex((item) => item.id === over.id)
      const newProfiles = arrayMove(shownProfiles, oldIndex, newIndex)

      try {
        dispatch(showLoadingOverlay())
        setShownProfiles(newProfiles)
        await updateUserTableProfile.mutateAsync({
          id: userTableProfileResponse?.data?._id,
          data: {
            profiles: newProfiles,
          },
        })
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }
  }

  const handleSetDefaultProfile = async (profileId) => {
    try {
      dispatch(showLoadingOverlay())

      const userTableProfile = userTableProfileResponse?.data?._id

      if (!userTableProfile) {
        throw new Error('User Table Profile id not found.')
      }

      const res = await makeDefaultProfile.mutateAsync({
        id: userTableProfileResponse?.data?._id,
        tableProfileId: profileId,
      })
    } catch (error) {
      toast.error('Error making default profile: ' + error.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <BaseOfferTaskWidget title={'My Sourcing Profile'} maxH="1200px">
      <Flex gap="2" direction={'column'} overflowY={{ base: 'visible', md: 'auto' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={shownProfiles} strategy={verticalListSortingStrategy}>
            {shownProfiles.map((profile) => (
              <DraggableItem key={profile._id} id={profile._id}>
                <Flex
                  w="100%"
                  alignItems={['stretch', 'center']}
                  direction={['column', 'row']}
                  gap="3"
                  overflow={'hidden'}
                  px="2"
                >
                  {profile._id === defaultProfileId ? (
                    <Box>
                      <FaStar size="20" color={'#ECC94B'} />
                    </Box>
                  ) : (
                    <Box
                      _hover={{ color: 'yellow.500' }}
                      onClick={() => handleSetDefaultProfile(profile._id)}
                      color="gray.400"
                    >
                      <FaRegStar size="20" />
                    </Box>
                  )}
                  <Button
                    w={['100%', '50%']}
                    minH={'30px'}
                    colorScheme={profile?.isCreatedFromAdmin ? 'teal' : 'blue'}
                    onClick={() => handleGoToProfile(profile?._id)}
                    px="5"
                    textOverflow={'ellipsis'}
                  >
                    {profile?.profileName ?? '(No Profile Name)'}
                  </Button>
                  {!profile?.isCreatedFromAdmin && (
                    <Box
                      w={['100%', '170px']}
                      textAlign={'center'}
                      fontSize={'sm'}
                      color={shownProfileInSidebar.includes(profile._id) ? 'red.300' : 'teal.500'}
                      onClick={() => handleToggleShownProfileInSidebar(profile._id)}
                      borderColor={shownProfileInSidebar.includes(profile._id) ? 'red.300' : 'teal.500'}
                      borderWidth={'1px'}
                      p="2"
                      borderRadius={'md'}
                      _hover={{
                        color: 'white',
                        cursor: 'pointer',
                        bg: shownProfileInSidebar.includes(profile._id) ? 'red.300' : 'teal.500',
                      }}
                    >
                      {shownProfileInSidebar.includes(profile._id) ? 'Remove from Sidebar' : 'Add to Sidebar'}
                    </Box>
                  )}

                  <Spacer display={['none', 'block']} />

                  {!profile?.isCreatedFromAdmin ? (
                    <DeleteButton
                      alignSelf={['flex-end', 'auto']}
                      onClick={(e) => {
                        // e.stopPropagation()
                        handleDeleteProfile(profile._id, profile.profileName)
                      }}
                    />
                  ) : (
                    <Box w="24px" display={['none', 'block']}></Box>
                  )}
                </Flex>
              </DraggableItem>
            ))}
          </SortableContext>
        </DndContext>
      </Flex>
    </BaseOfferTaskWidget>
  )
}

export default MySourcingWidget
